/**
 * pi-nonograms - A game of nonograms
 * https://github.com/DomenicoDeFelice/Pi-Nonograms
 *
 * Copyright (c) 2013-2020 Domenico De Felice
 * Released under the GNU GPL v3 License
 *
 * @license
 * @format
 * @flow
 */

import type { GameOptions, Mode } from './Nonogram.js';
import nullthrows from 'nullthrows';

const Srand = require('seeded-rand');
const Event = require('./Event.js');

export type Cell = 'empty' | 'unknown' | 'filled';

type ModelEvents = {|
  guessChanged: Event,
  nonogramChanged: Event,
  nonogramSolved: Event,
  nonogramUnsolved: Event,
|};

type Sequence = {|
  length: number,
  solved: boolean,
|};

type Traversable = (
  ({| offset: number, actual: Cell, guess: Cell |}) => void
) => void;

export type Definition = Array<Sequence>;

class Model {
  /**
   * Events fired by the model:
   *   - guessChanged: The user guessed the content of a cell
   *   - nonogramChanged: The nonogram has been changed
   *   - nonogramSolved: The user solved the nonogram
   *   - nonogramUnsolved: The nonogram was solved but now it isn't
   */
  events: ModelEvents = {
    guessChanged: new Event(this),
    nonogramChanged: new Event(this),
    nonogramSolved: new Event(this),
    nonogramUnsolved: new Event(this),
  };

  width: number;
  height: number;
  _srand: Srand;
  _mode: Mode;
  _actual: Array<?Cell>;
  _guess: Array<?Cell>;
  _solved: boolean;

  constructor(opts: GameOptions) {
    this.width = opts.width;
    this.height = opts.height;
    this._srand = opts.srand;

    this._setupNonogram();
    this.setMode(opts.mode);
  }

  /**
   * Return the state of a cell.
   *
   * If `x` and `y` are both provided they are the coordinates of the cell.
   * If only `x` is provided, it is the index of the cell.
   */
  getCellAt = (x: number, y?: number): Cell => {
    return this._actual[this._indexFromXY(x, y)] ?? 'empty';
  };

  getGuessAt = (x: number, y?: number): Cell => {
    return this._guess[this._indexFromXY(x, y)] ?? 'unknown';
  };

  setGuessAt = (x: number, yOrGuess: number | Cell, guess?: Cell) => {
    let index;

    if (guess == null) {
      // Shift arguments
      guess = ((yOrGuess: any): Cell);
      index = x;
    } else {
      index = this._indexFromXY(x, ((yOrGuess: any): number));
    }

    const oldGuess = this.getGuessAt(index);
    this._guess[index] = guess;

    this.events.guessChanged.notify({
      x: x,
      y: yOrGuess,
      oldGuess: oldGuess,
      newGuess: guess,
    });

    if (this._mode === 'play') {
      this._checkIfSolved();
    }
  };

  getMode = (): Mode => {
    return this._mode;
  };

  setMode = (mode: Mode) => {
    if (mode === this._mode) {
      return;
    }

    this._mode = mode;

    if (mode === 'draw') {
      this._guess = this._actual;
    } else {
      const nCells = this.width * this.height;
      this._guess = new Array(nCells);
    }

    this._setUnsolved();
    this.events.nonogramChanged.notify();
  };

  isSolved = (): boolean => {
    return this._solved;
  };

  // TO-DO: Implement a Traversable interface that lets you traverse rows or columns in an abstract way.

  getRowDefinition = (row: number): Definition => {
    const definition = [];

    let sequenceBegin;
    let sequenceEnd;
    let sequenceSolved;
    let sequenceLength = 0;

    const mode = this._mode;

    for (let col = 0; col < this.width; col++) {
      if (this.getCellAt(col, row) === 'filled') {
        if (sequenceLength === 0) {
          sequenceBegin = col;
        }

        sequenceLength++;
        continue;
      }

      if (sequenceLength === 0) {
        continue;
      }

      sequenceEnd = col - 1;
      sequenceSolved = false;

      if (mode === 'play') {
        sequenceBegin = nullthrows(sequenceBegin);
        if (
          (sequenceBegin === 0 ||
            this.getGuessAt(sequenceBegin - 1, row) === 'empty') &&
          (sequenceEnd === this.width - 1 ||
            this.getGuessAt(sequenceEnd + 1, row) === 'empty')
        ) {
          sequenceSolved = true;
          for (let index = sequenceBegin; index <= sequenceEnd; index++) {
            if (this.getGuessAt(index, row) !== 'filled') {
              sequenceSolved = false;
              break;
            }
          }
        }
      }

      definition.push({
        length: sequenceLength,
        solved: sequenceSolved,
      });

      sequenceLength = 0;
    }

    if (sequenceLength > 0) {
      sequenceEnd = this.width - 1;

      sequenceSolved = false;

      if (mode === 'play') {
        sequenceBegin = nullthrows(sequenceBegin);
        if (
          (sequenceBegin === 0 ||
            this.getGuessAt(sequenceBegin - 1, row) === 'empty') &&
          (sequenceEnd === this.width - 1 ||
            this.getGuessAt(sequenceEnd + 1, row) === 'empty')
        ) {
          sequenceSolved = true;
          for (let index = sequenceBegin; index <= sequenceEnd; index++) {
            if (this.getGuessAt(index, row) !== 'filled') {
              sequenceSolved = false;
              break;
            }
          }
        }
      }

      definition.push({
        length: sequenceLength,
        solved: sequenceSolved,
      });
    }
    return definition;
  };

  getColumnDefinition = (col: number): Definition => {
    const definition = [];

    let sequenceBegin;
    let sequenceEnd;
    let sequenceSolved;
    let sequenceLength = 0;

    const mode = this._mode;

    let row;
    for (row = 0; row < this.height; row++) {
      if (this.getCellAt(col, row) === 'filled') {
        if (sequenceLength === 0) {
          sequenceBegin = row;
        }
        sequenceLength++;
        continue;
      }

      if (sequenceLength === 0) {
        continue;
      }

      sequenceEnd = row - 1;

      sequenceSolved = false;

      if (mode === 'play') {
        sequenceBegin = nullthrows(sequenceBegin);
        if (
          (sequenceBegin === 0 ||
            this.getGuessAt(col, sequenceBegin - 1) === 'empty') &&
          (sequenceEnd === this.height - 1 ||
            this.getGuessAt(col, sequenceEnd + 1) === 'empty')
        ) {
          sequenceSolved = true;
          for (let index = sequenceBegin; index <= sequenceEnd; index++) {
            if (this.getGuessAt(col, index) !== 'filled') {
              sequenceSolved = false;
              break;
            }
          }
        }
      }

      definition.push({
        length: sequenceLength,
        solved: sequenceSolved,
      });

      sequenceLength = 0;
    }

    if (sequenceLength > 0) {
      sequenceEnd = row - 1;

      sequenceSolved = false;

      if (mode === 'play') {
        sequenceBegin = nullthrows(sequenceBegin);
        if (
          (sequenceBegin === 0 ||
            this.getGuessAt(col, sequenceBegin - 1) === 'empty') &&
          (sequenceEnd === this.height - 1 ||
            this.getGuessAt(col, sequenceEnd + 1) === 'empty')
        ) {
          sequenceSolved = true;
          for (let index = sequenceBegin; index <= sequenceEnd; index++) {
            if (this.getGuessAt(col, index) !== 'filled') {
              sequenceSolved = false;
              break;
            }
          }
        }
      }

      definition.push({
        length: sequenceLength,
        solved: sequenceSolved,
      });
    }

    return definition;
  };

  giveHint = (): void => {
    // Is there any hint to give?
    if (this.isSolved() || this._mode !== 'play') {
      return;
    }

    // Let's first check for errors
    const nCells = this.width * this.height;
    const actual = this._actual;
    const guess = this._guess;
    let xy, x, y;

    for (let index = nCells; index--; ) {
      if (
        (guess[index] === 'filled' && actual[index] !== 'filled') ||
        (guess[index] === 'empty' && actual[index] === 'filled')
      ) {
        // Error found, correct it
        xy = this._XYFromIndex(index);
        x = xy[0];
        y = xy[1];
        this.setGuessAt(x, y, this.getCellAt(x, y));
        return;
      }
    }

    // No errors found, let's complete a cell
    do {
      xy = this._getRandomXY();
      x = xy[0];
      y = xy[1];
    } while (this.getGuessAt(x, y) !== 'unknown');
    this.setGuessAt(x, y, this.getCellAt(x, y));
  };

  randomize = (density: number): void => {
    this._setupNonogram();
    this._mode = 'play';

    const actual = this._actual;
    const nCells = this.width * this.height;
    let toBeFilled = Math.min(Math.floor(nCells * density), nCells);

    while (toBeFilled) {
      let index = this._getRandomIndex();
      if (actual[index] == null) {
        actual[index] = 'filled';
        toBeFilled--;
      }
    }

    this.events.nonogramChanged.notify();
  };

  resetGuesses = () => {
    const nCells = this.width * this.height;

    this._guess = new Array(nCells);
    if (this._mode === 'draw') {
      this._actual = this._guess;
    }

    this._solved = false;

    this.events.nonogramChanged.notify();
  };

  // Private methods

  _setupNonogram = () => {
    const nCells = this.width * this.height;
    this._actual = new Array(nCells);
    this._guess = new Array(nCells);
    this._setUnsolved();
  };

  _indexFromXY = (x: number, y?: number): number => {
    if (y == null) {
      return x;
    }

    return y * this.width + x;
  };

  _XYFromIndex = (index: number): [number, number] => {
    const width = this.width;
    const y = Math.floor(index / width);
    const x = index % width;

    return [x, y];
  };

  _getRandomIndex = (): number => {
    const nCells = this.width * this.height;
    return this._srand.intInRange(0, nCells - 1);
  };

  _getRandomXY = (): [number, number] => {
    const x = this._srand.intInRange(0, this.width - 1);
    const y = this._srand.intInRange(0, this.height - 1);
    return [x, y];
  };

  _setSolved = (): void => {
    if (!this.isSolved()) {
      this._solved = true;
      this.events.nonogramSolved.notify();
    }
  };

  _setUnsolved = (): void => {
    if (this.isSolved()) {
      this._solved = false;
      this.events.nonogramUnsolved.notify();
    }
  };

  _checkIfSolved = (): void => {
    const actual = this._actual;
    const guess = this._guess;

    for (let index = actual.length; index--; ) {
      if (
        (actual[index] === 'filled' && guess[index] !== 'filled') ||
        (actual[index] !== 'filled' && guess[index] === 'filled')
      ) {
        this._setUnsolved();
        return;
      }
    }
    this._setSolved();
  };
}

module.exports = Model;
